<template>
  <div class="relative">
    <input
      :id="inputId"
      :value="displayValue"
      type="text"
      readonly
      @click="showPicker = !showPicker"
      :placeholder="placeholder"
      :class="[
        'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 pr-10 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer',
        inputClass
      ]"
    />
    <button
      type="button"
      @click="showPicker = !showPicker"
      class="absolute right-0 top-0 h-9 w-9 flex items-center justify-center text-muted-foreground hover:text-foreground"
    >
      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    </button>
    <!-- Date Picker Popup -->
    <div
      v-if="showPicker"
      class="absolute z-50 mt-2 w-64 bg-card border rounded-lg shadow-lg"
    >
      <div class="p-4">
        <div class="flex items-center justify-between mb-4">
          <button
            type="button"
            @click="previousMonth"
            class="p-1 hover:bg-muted rounded"
          >
            <svg
              class="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <span class="font-medium">{{ currentMonthYear }}</span>
          <button
            type="button"
            @click="nextMonth"
            class="p-1 hover:bg-muted rounded"
          >
            <svg
              class="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
        <div class="grid grid-cols-7 gap-1 mb-2">
          <div class="text-center text-xs font-medium text-muted-foreground py-1">
            S
          </div>
          <div class="text-center text-xs font-medium text-muted-foreground py-1">
            M
          </div>
          <div class="text-center text-xs font-medium text-muted-foreground py-1">
            T
          </div>
          <div class="text-center text-xs font-medium text-muted-foreground py-1">
            W
          </div>
          <div class="text-center text-xs font-medium text-muted-foreground py-1">
            T
          </div>
          <div class="text-center text-xs font-medium text-muted-foreground py-1">
            F
          </div>
          <div class="text-center text-xs font-medium text-muted-foreground py-1">
            S
          </div>
        </div>
        <div class="grid grid-cols-7 gap-1">
          <button
            v-for="day in calendarDays"
            :key="day.date"
            type="button"
            @click="selectDate(day.date)"
            :disabled="day.disabled"
            :class="[
              'text-sm py-1 rounded hover:bg-muted disabled:opacity-25 disabled:cursor-not-allowed',
              day.isToday ? 'bg-primary text-primary-foreground font-medium' : '',
              day.isSelected ? 'bg-blue-500 text-white font-medium' : '',
            ]"
          >
            {{ day.day }}
          </button>
        </div>
        <div class="flex justify-end pt-2 border-t mt-2">
          <button
            type="button"
            @click="clearDate"
            class="text-xs text-muted-foreground hover:text-foreground px-2 py-1"
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

interface Props {
  modelValue: string | null
  placeholder?: string
  inputId?: string
  inputClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Select date...',
  inputId: 'date-picker',
  inputClass: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: string | null]
}>()

// State
const showPicker = ref(false)
const selectedDate = ref(new Date())
const currentMonth = ref(new Date().getMonth())
const currentYear = ref(new Date().getFullYear())

// Computed properties
const displayValue = computed(() => {
  if (!props.modelValue) return ''
  // Parse date string manually to avoid timezone issues
  // modelValue is in YYYY-MM-DD format
  const [year, month, day] = props.modelValue.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
})

const currentMonthYear = computed(() => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  return `${monthNames[currentMonth.value]} ${currentYear.value}`
})

const calendarDays = computed(() => {
  const year = currentYear.value
  const month = currentMonth.value
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const today = new Date()
  const selectedDateStr = props.modelValue

  const days = []

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDay; i++) {
    days.push({ day: '', date: '', disabled: true, isToday: false, isSelected: false })
  }

  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day)
    // Format date string manually to avoid timezone issues
    // Use local date components directly instead of toISOString()
    const monthStr = String(month + 1).padStart(2, '0')
    const dayStr = String(day).padStart(2, '0')
    const dateStr = `${year}-${monthStr}-${dayStr}`
    const isToday = date.toDateString() === today.toDateString()
    const isSelected = dateStr === selectedDateStr

    days.push({ day, date: dateStr, disabled: false, isToday, isSelected })
  }

  return days
})

// Methods
const previousMonth = () => {
  if (currentMonth.value === 0) {
    currentMonth.value = 11
    currentYear.value--
  } else {
    currentMonth.value--
  }
}

const nextMonth = () => {
  if (currentMonth.value === 11) {
    currentMonth.value = 0
    currentYear.value++
  } else {
    currentMonth.value++
  }
}

const selectDate = (date: string) => {
  emit('update:modelValue', date)
  showPicker.value = false
}

// Add a clear button or allow clearing the date
const clearDate = () => {
  emit('update:modelValue', null)
  showPicker.value = false
}
</script>
